/*
 * ProductForm — ES module replacement for legacy `theme.Product` from
 * assets/theme.js (now deleted).
 *
 * One instance per `[data-section-type="product"]` container. Owns the
 * variant-pick lifecycle:
 *   - Constructs `new Variants(options)` (imported from components/variants.js)
 *   - Listens for `variantChange`, `variantPriceChange`,
 *     `variantUnitPriceChange`, `variantSKUChange` events dispatched on the
 *     container, and updates the matching DOM hooks accordingly
 *   - Wires color-swatch radio changes to the readout label
 *
 * Modern features NOT owned by this module (handled elsewhere):
 *   - Image gallery / variant-image swap → snippets/product-images.liquid
 *   - Form submission / add-to-cart → global ATC interceptor in
 *     layout/theme.liquid (routes through assets/components/cart.js)
 *   - Quantity selector → <quantity-input> Web Component
 *
 * Self-registers on DOMContentLoaded and `shopify:section:load`.
 * Idempotent — destroys + recreates the picker on section reload so the
 * theme editor stays in sync.
 */

import { Variants } from '@/components/variants';

const ATTR = 'data-section-type="product"';

const SELECTORS = {
  variantsJson:        '[data-variant-json]',
  form:                '.product-single__form',
  priceWrapper:        '[data-product-price-wrap]',
  price:               '[data-product-price]',
  comparePrice:        '[data-compare-price]',
  savePrice:           '[data-save-price]',
  priceA11y:           '[data-a11y-price]',
  comparePriceA11y:    '[data-compare-price-a11y]',
  unitWrapper:         '[data-unit-price-wrapper]',
  unitPrice:           '[data-unit-price]',
  unitPriceBaseUnit:   '[data-unit-base]',
  sku:                 '[data-sku]',
  inventory:           '[data-product-inventory]',
  incomingInventory:   '[data-incoming-inventory]',
  colorLabel:          '[data-variant-color-label]',
  addToCart:           '[data-add-to-cart]',
  addToCartText:       '[data-add-to-cart-text]',
  originalSelectorId:  '[data-product-select]',
  singleOptionSelector:'[data-variant-input]',
  variantColorSwatch:  '.variant__input--color-swatch',
};

const CLASSES = {
  onSale:   'on-sale',
  disabled: 'disabled',
  hide:     'hide',
};

/* ---------- helpers ---------- */

function getMoneyFormat() {
  return (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}';
}
function getStrings() {
  return (window.theme && window.theme.strings) || {};
}

/**
 * Formats `cents` (integer) using the merchant's Shopify money format
 * template (e.g. `${{amount}}` or `${{amount_no_decimals}}`). This mirrors
 * the legacy `theme.Currency.formatMoney` so the same template strings
 * configured in theme settings continue to work unchanged.
 *
 * @param {number|string} cents
 * @param {string} [format]
 * @returns {string}
 */
function formatMoney(cents, format) {
  format = format || getMoneyFormat();
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(number) || number == null) return '0';
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const decimals = parts[1] ? decimal + parts[1] : '';
    return dollars + decimals;
  }

  const match = format.match(placeholderRegex);
  if (!match) return format;
  let value = '';
  switch (match[1]) {
    case 'amount':                                value = withDelimiters(cents, 2); break;
    case 'amount_no_decimals':                    value = withDelimiters(cents, 0); break;
    case 'amount_with_comma_separator':           value = withDelimiters(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
    case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
    default:                                       value = withDelimiters(cents, 2);
  }
  return format.replace(placeholderRegex, value);
}

function getBaseUnit(variant) {
  if (!variant || !variant.unit_price_measurement || !variant.unit_price_measurement.reference_value) return '';
  const m = variant.unit_price_measurement;
  return m.reference_value === 1 ? m.reference_unit : m.reference_value + m.reference_unit;
}

/* ---------- ProductForm ---------- */

export class ProductForm {
  constructor(container) {
    this.container = container;
    this.sectionId = container.getAttribute('data-section-id');
    this.productId = container.getAttribute('data-product-id') || (container.dataset && container.dataset.productId);

    this.namespace = '.product-' + this.sectionId;
    this.settings = {
      enableHistoryState: container.getAttribute('data-enable-history-state') !== 'false',
      saveType: 'percent',
      inventory: false,
      inventoryThreshold: 0,
    };

    this._cache = {};
    this.variantsObject = null;
    this.variants = null;

    /* Read settings exposed by main-product.liquid as data-* attributes. */
    if (container.dataset.saveType) this.settings.saveType = container.dataset.saveType;

    this._handlers = {};
    this._init();
  }

  destroy() {
    /* Detach event listeners — picker doesn't expose destroy() but we own the
       listeners on the container, so removing them is enough. */
    if (this._handlers.cartBtn) this.container.removeEventListener('variantChange', this._handlers.cartBtn);
    if (this._handlers.price)   this.container.removeEventListener('variantPriceChange', this._handlers.price);
    if (this._handlers.unit)    this.container.removeEventListener('variantUnitPriceChange', this._handlers.unit);
    if (this._handlers.sku)     this.container.removeEventListener('variantSKUChange', this._handlers.sku);
    if (this._handlers.inv)     this.container.removeEventListener('variantChange', this._handlers.inv);
    this._cache = {};
  }

  _cacheElements() {
    const c = this.container;
    this._cache = {
      form:             c.querySelector(SELECTORS.form),
      priceWrapper:     c.querySelector(SELECTORS.priceWrapper),
      price:            c.querySelector(SELECTORS.price),
      comparePrice:     c.querySelector(SELECTORS.comparePrice),
      savePrice:        c.querySelector(SELECTORS.savePrice),
      priceA11y:        c.querySelector(SELECTORS.priceA11y),
      comparePriceA11y: c.querySelector(SELECTORS.comparePriceA11y),
      unitWrapper:      c.querySelector(SELECTORS.unitWrapper),
      unitPrice:        c.querySelector(SELECTORS.unitPrice),
      unitPriceBase:    c.querySelector(SELECTORS.unitPriceBaseUnit),
      sku:              c.querySelector(SELECTORS.sku),
      inventory:        c.querySelector(SELECTORS.inventory),
      incomingInventory:c.querySelector(SELECTORS.incomingInventory),
      addToCart:        c.querySelector(SELECTORS.addToCart),
      addToCartText:    c.querySelector(SELECTORS.addToCartText),
    };

    /* Inventory display config from markup data attributes. */
    if (this._cache.inventory) {
      this.settings.inventory = true;
      this.settings.inventoryThreshold = parseInt(this._cache.inventory.dataset.threshold || '0', 10) || 0;
    }
  }

  _init() {
    this._cacheElements();
    this._initVariants();
    this._initColorSwatches();
  }

  _initVariants() {
    const variantJson = this.container.querySelector(SELECTORS.variantsJson);
    if (!variantJson) return;

    try {
      this.variantsObject = JSON.parse(variantJson.innerHTML);
    } catch (e) {
      console.warn('[ProductForm] Failed to parse variant JSON', e);
      return;
    }

    this.variants = new Variants({
      container: this.container,
      enableHistoryState: this.settings.enableHistoryState,
      singleOptionSelector: SELECTORS.singleOptionSelector,
      originalSelectorId: SELECTORS.originalSelectorId,
      variants: this.variantsObject,
    });

    /* Wire event handlers — keep references so destroy() can detach. */
    this._handlers.cartBtn = (evt) => this._updateCartButton(evt);
    this._handlers.price   = (evt) => this._updatePrice(evt);
    this._handlers.unit    = (evt) => this._updateUnitPrice(evt);
    this._handlers.sku     = (evt) => this._updateSku(evt);
    this._handlers.inv     = (evt) => this._updateInventory(evt);

    this.container.addEventListener('variantChange',          this._handlers.cartBtn);
    this.container.addEventListener('variantPriceChange',     this._handlers.price);
    this.container.addEventListener('variantUnitPriceChange', this._handlers.unit);
    if (this._cache.sku) this.container.addEventListener('variantSKUChange', this._handlers.sku);
    if (this._cache.inventory) this.container.addEventListener('variantChange', this._handlers.inv);
  }

  _initColorSwatches() {
    const swatches = this.container.querySelectorAll(SELECTORS.variantColorSwatch);
    swatches.forEach((swatch) => {
      swatch.addEventListener('change', () => {
        const color = swatch.dataset.colorName;
        const index = swatch.dataset.colorIndex;
        this._updateColorName(color, index);
      });
    });
  }

  _updateColorName(color, index) {
    const label = this.container.querySelector(SELECTORS.colorLabel + `[data-index="${index}"]`);
    if (label) label.textContent = color;
  }

  _updateCartButton(evt) {
    const variant = evt.detail.variant;
    const btn = this._cache.addToCart;
    const btnText = this._cache.addToCartText;
    if (!btn || !btnText) return;

    const strings = getStrings();
    if (variant) {
      if (variant.available) {
        btn.classList.remove(CLASSES.disabled);
        btn.disabled = false;
        btnText.textContent = btnText.dataset.defaultText || strings.addToCart || 'Add to cart';
      } else {
        btn.classList.add(CLASSES.disabled);
        btn.disabled = true;
        btnText.textContent = strings.soldOut || 'Sold out';
      }
    } else {
      btn.classList.add(CLASSES.disabled);
      btn.disabled = true;
      btnText.textContent = strings.unavailable || 'Unavailable';
    }
  }

  _updatePrice(evt) {
    const variant = evt.detail.variant;
    if (!variant) return;
    const fmt = getMoneyFormat();
    const cache = this._cache;

    if (cache.price) cache.price.innerHTML = formatMoney(variant.price, fmt);

    if (variant.compare_at_price > variant.price) {
      if (cache.comparePrice)     cache.comparePrice.innerHTML = formatMoney(variant.compare_at_price, fmt);
      if (cache.priceWrapper)     cache.priceWrapper.classList.remove(CLASSES.hide);
      if (cache.price)            cache.price.classList.add(CLASSES.onSale);
      if (cache.comparePriceA11y) cache.comparePriceA11y.setAttribute('aria-hidden', 'false');
      if (cache.priceA11y)        cache.priceA11y.setAttribute('aria-hidden', 'false');

      let savings = variant.compare_at_price - variant.price;
      if (this.settings.saveType === 'percent') {
        savings = Math.round((savings * 100) / variant.compare_at_price) + '%';
      } else {
        savings = formatMoney(savings, fmt);
      }
      if (cache.savePrice) {
        cache.savePrice.classList.remove(CLASSES.hide);
        const tpl = (getStrings().savePrice || 'Save [saved_amount]');
        cache.savePrice.innerHTML = tpl.replace('[saved_amount]', savings);
      }
    } else {
      if (cache.priceWrapper)     cache.priceWrapper.classList.add(CLASSES.hide);
      if (cache.savePrice)        cache.savePrice.classList.add(CLASSES.hide);
      if (cache.price)            cache.price.classList.remove(CLASSES.onSale);
      if (cache.comparePriceA11y) cache.comparePriceA11y.setAttribute('aria-hidden', 'true');
      if (cache.priceA11y)        cache.priceA11y.setAttribute('aria-hidden', 'true');
    }
  }

  _updateUnitPrice(evt) {
    const variant = evt.detail.variant;
    const cache = this._cache;
    if (variant && variant.unit_price) {
      if (cache.unitPrice)     cache.unitPrice.innerHTML = formatMoney(variant.unit_price, getMoneyFormat());
      if (cache.unitPriceBase) cache.unitPriceBase.innerHTML = getBaseUnit(variant);
      if (cache.unitWrapper)   cache.unitWrapper.classList.remove(CLASSES.hide);
    } else if (cache.unitWrapper) {
      cache.unitWrapper.classList.add(CLASSES.hide);
    }
  }

  _updateSku(evt) {
    const variant = evt.detail.variant;
    if (!variant || !this._cache.sku) return;
    this._cache.sku.textContent = variant.sku || '';
  }

  _updateInventory(evt) {
    const variant = evt.detail.variant;
    /* If no inventory management or "continue" selling policy, hide stock UI. */
    if (!variant || !variant.inventory_management || variant.inventory_policy === 'continue') {
      this._toggleInventoryQuantity(variant, false);
      this._toggleIncomingInventory(false);
      return;
    }
    /* Per-variant inventory data is normally injected by the section as
       `window.inventories[productId][variantId]`. Honor that contract. */
    const inv = window.inventories && this.productId && window.inventories[this.productId] && window.inventories[this.productId][variant.id];
    if (!inv) return;
    if (inv.policy === 'continue') {
      this._toggleInventoryQuantity(variant, false);
      this._toggleIncomingInventory(false);
      return;
    }
    const quantity = inv.quantity;
    const showInventory = quantity > 0 && quantity <= this.settings.inventoryThreshold;
    this._toggleInventoryQuantity(variant, showInventory, quantity);
    const showIncoming = !showInventory && inv.incoming === 'true' && quantity <= this.settings.inventoryThreshold;
    this._toggleIncomingInventory(showIncoming, variant.available, inv.next_incoming_date);
  }

  _toggleInventoryQuantity(variant, show, qty) {
    const el = this._cache.inventory;
    if (!el) return;
    const strings = getStrings();
    const salesPoint = el.closest('.product-block');

    if (parseInt(qty, 10) <= this.settings.inventoryThreshold) {
      el.parentNode.classList.add('inventory--low');
      el.textContent = (strings.stockLabel || 'Only [count] left').replace('[count]', qty);
    } else {
      el.parentNode.classList.remove('inventory--low');
      el.textContent = strings.inStockLabel || 'In stock';
    }

    if (variant && variant.available && show) {
      el.parentNode.classList.remove(CLASSES.hide);
      if (salesPoint) salesPoint.classList.remove(CLASSES.hide);
    } else {
      el.parentNode.classList.add(CLASSES.hide);
      if (salesPoint) salesPoint.classList.add(CLASSES.hide);
    }
  }

  _toggleIncomingInventory(show, available, date) {
    const el = this._cache.incomingInventory;
    if (!el) return;
    const textEl = el.querySelector('.js-incoming-text');
    const strings = getStrings();
    const salesPoint = el.closest('.product-block');

    if (show) {
      let str;
      if (!date)        str = strings.waitingForStock     || 'Waiting for new stock';
      else if (available) str = (strings.willNotShipUntil || 'Will not ship until [date]').replace('[date]', date);
      else                str = (strings.willBeInStockAfter || 'Will be back in stock after [date]').replace('[date]', date);
      el.classList.remove(CLASSES.hide);
      if (salesPoint) salesPoint.classList.remove(CLASSES.hide);
      if (textEl) textEl.textContent = str;
    } else {
      el.classList.add(CLASSES.hide);
    }
  }
}

/* ---------- Self-registration ---------- */

const instances = new WeakMap();

function attachAll(root) {
  (root || document).querySelectorAll('[' + ATTR + ']').forEach((el) => {
    const prior = instances.get(el);
    if (prior) {
      try { prior.destroy(); } catch (e) { /* ignore */ }
    }
    instances.set(el, new ProductForm(el));
  });
}

if (document.readyState !== 'loading') {
  attachAll(document);
} else {
  document.addEventListener('DOMContentLoaded', () => attachAll(document));
}

document.addEventListener('shopify:section:load', (e) => {
  if (e.target && e.target.querySelector('[' + ATTR + ']')) attachAll(e.target);
});

export default ProductForm;
